import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import {Am5Settings} from "@/mixins/am5charts/Am5Settings";
import {markRaw} from "vue";
import * as _ from "lodash";

const isValidDate = dateObject => new Date(dateObject).toString() !== 'Invalid Date';

export const Am5DateAxis = class {
    constructor(root, start, end, timezone) {
        // Owns a live am5 DateAxis — never let Vue proxy it. See the note in
        // Am5Series' constructor for why a second identity strands canvas
        // displays and produces ghost series.
        markRaw(this)
        this.create(root, start, end, timezone)
    }

    getAxis() {
        return this.axis
    }

    setTimezone(timezone) {
        this.timezone = timezone
        if (!_.isNil(this.root)) {
            this.root.timezone = am5.Timezone.new(timezone)
        }
    }

    setSeries(series) {
        this.series = series
    }

    updateDateAxisInterval() {
        let numberVisibleSeries = 0
        let finishEarly = false
        _.forEach(this.series, series => {
            if (!series.visible) {
                return
            }
            if (finishEarly || series.isLoadingData()) {
                finishEarly = true
                return
            }

            numberVisibleSeries++
        })

        if (finishEarly) {
            this.updateGapAndMinBulletDistance()
            return
        }

        if (numberVisibleSeries > 0 && !_.isNil(this.axis)) {
            this.axis.set("min", this.start.getTime())
            this.axis.set("max", this.end.getTime())

            let scrollAxis = this.getScrollAxisIfExists()
            if (!_.isNil(scrollAxis)) {
                scrollAxis.set("min", this.start.getTime())
                scrollAxis.set("max", this.end.getTime())
            }
        }

        this.updateGapAndMinBulletDistance()
    }

    updateTimeIntervals(start = null, end = null, updateSeries = true) {
        this.start = _.isNil(start) ? this.start : start
        this.end = _.isNil(end) ? this.end : end

        if (!_.isNil(this.axis)) {
            this.axis.setAll({
                baseInterval: { timeUnit: 'second', count: 1 },
            })

            // Update the scrollbar axis to span the full requested range
            let scrollAxis = this.getScrollAxisIfExists()
            if (!_.isNil(scrollAxis)) {
                scrollAxis.setAll({
                    min: this.start.getTime(),
                    max: this.end.getTime(),
                })
            }
        }

        if (!updateSeries) {
            return
        }

        _.forEach(this.series, series => {
            series.updateInterval(this.start, this.end)
        })
    }

    buildAndRender(chart) {
        this.dispose()
        this.chart = chart

        this.axis = chart.xAxes.push(
            am5xy.DateAxis.new(this.root, {
                baseInterval: { timeUnit: 'second', count: 1 },
                groupData: false,
                strictMinMax: true,
                maxDeviation: 0,
                min: this.start.getTime(),
                max: this.end.getTime(),
                renderer: am5xy.AxisRendererX.new(this.root, {
                    minGridDistance: 60,
                }),
                gridIntervals: [
                    { timeUnit: 'second', count: 1 },
                    { timeUnit: 'minute', count: 1 },
                    { timeUnit: 'minute', count: 5 },
                    { timeUnit: 'minute', count: 15 },
                    { timeUnit: 'minute', count: 60 },
                    { timeUnit: 'hour', count: 4 },
                    { timeUnit: 'day', count: 1 },
                    { timeUnit: 'day', count: 7 },
                    { timeUnit: 'day', count: 14 },
                    { timeUnit: 'month', count: 1 },
                    { timeUnit: 'month', count: 3 },
                    { timeUnit: 'month', count: 6 },
                    { timeUnit: 'year', count: 1 },
                ],
                groupIntervals: [
                    { timeUnit: 'minute', count: 1 },
                    { timeUnit: 'minute', count: 5 },
                    { timeUnit: 'minute', count: 10 },
                    { timeUnit: 'minute', count: 15 },
                    { timeUnit: 'minute', count: 30 },
                    { timeUnit: 'hour', count: 1 },
                    { timeUnit: 'hour', count: 2 },
                    { timeUnit: 'hour', count: 4 },
                    { timeUnit: 'hour', count: 6 },
                    { timeUnit: 'hour', count: 12 },
                    { timeUnit: 'day', count: 1 },
                    { timeUnit: 'day', count: 2 },
                    { timeUnit: 'day', count: 7 },
                    { timeUnit: 'month', count: 1 },
                ],
                tooltip: am5.Tooltip.new(this.root, {}),
                tooltipIntervalOffset: 0,
            })
        )

        // Set timezone on root
        this.root.timezone = am5.Timezone.new(this.timezone)

        this.axis.get("renderer").grid.template.set("location", 0)
        this.axis.get("renderer").labels.template.setAll({
            location: 0,
            multiLocation: 0,
        })

        // Setup tooltip adapter
        this.buildDateAxisTooltip()

        // Setup grouping
        this.buildDateAxisGrouping()
    }

    buildDateAxisTooltip() {
        this.axis.set("tooltipDateFormat", "M/d/yyyy h:mm:ss aa")
    }

    buildDateAxisGrouping() {
        // In am5, grouping is configured via the axis settings (already done in buildAndRender)
        // We listen for axis range changes to adjust grouping intervals dynamically
        this.axis.onPrivate("selectionMin", () => {
            this._handleDataRangeChanged()
        })
        this.axis.onPrivate("selectionMax", () => {
            this._handleDataRangeChanged()
        })
    }

    _handleDataRangeChanged() {
        if (_.isNil(this.axis)) {
            return
        }

        setTimeout(() => {
            this.updateGapAndMinBulletDistance()
        }, 100)
    }

    axisInitialized() {
        return !_.isNil(this.axis)
    }

    getScrollAxisIfExists() {
        if (_.isNil(this.chart)) {
            return null
        }

        let scrollbarX = this.chart.get("scrollbarX")
        if (_.isNil(scrollbarX)) {
            return null
        }

        let sbChart = scrollbarX.chart
        if (_.isNil(sbChart) || sbChart.xAxes.length === 0) {
            return null
        }

        return sbChart.xAxes.getIndex(0)
    }

    getIntervals(startDate = null, endDate = null, ratioUsed = 1) {
        startDate = _.isNil(startDate) || !isValidDate(startDate)
                ? this.start
                : startDate
        endDate = _.isNil(endDate) || !isValidDate(endDate)
                ? this.end
                : endDate
        let groupCount = Am5Settings.GROUP_COUNT
        let delta = (endDate.getTime() - startDate.getTime()) / 1000
        let deltaRatio = delta / groupCount * ratioUsed

        let intervals = {
            1: { timeUnit: "second", count: 1 },
            5: { timeUnit: "second", count: 5 },
            15: { timeUnit: "second", count: 15 },
            30: { timeUnit: "second", count: 30 },
            60: { timeUnit: "minute", count: 1 },
            300: { timeUnit: "minute", count: 5 },
            600: { timeUnit: "minute", count: 10 },
            900: { timeUnit: "minute", count: 15 },
            1800: { timeUnit: "minute", count: 30 },
            3600: { timeUnit: "hour", count: 1 },
            14400: { timeUnit: "hour", count: 4 },
            86400: { timeUnit: "day", count: 1 },
            604800: { timeUnit: "week", count: 1 },
            1209600: { timeUnit: "week", count: 2 },
            2628000: { timeUnit: "month", count: 1 },
            10512000: { timeUnit: "month", count: 4 },
            15768000: { timeUnit: "month", count: 6 },
            31536000: { timeUnit: "year", count: 1 },
        }

        let desiredIntervalSeconds = null
        Object.keys(intervals).forEach((numSeconds) => {
            if (_.isNil(desiredIntervalSeconds) && deltaRatio < numSeconds) {
                desiredIntervalSeconds = numSeconds
            }
        })

        return [
            intervals[desiredIntervalSeconds],
        ]
    }

    updateGapAndMinBulletDistance() {
        if (_.isNil(this.chart)) {
            return
        }

        let scrollbarX = this.chart.get("scrollbarX")
        let scrollSeries = null
        if (!_.isNil(scrollbarX)) {
            let sbChart = scrollbarX.chart
            if (!_.isNil(sbChart) && sbChart.series.length > 0) {
                scrollSeries = sbChart.series.getIndex(0)
            }
        }

        let multiplier = 1
        let baseInterval = this.axis.get("baseInterval")
        if (baseInterval) {
            let interval = baseInterval.timeUnit
            if (interval === 'second') {
                multiplier = 60
            } else if (interval === 'hour') {
                multiplier = 1.0 / 60
            } else if (interval === 'day') {
                multiplier = 1.0 / 60 / 24
            }
        }

        let gapCount = Am5Settings.useShortGaps ? Am5Settings.SHORT_GAP_LENGTH * multiplier : Am5Settings.LONG_GAP_LENGTH * multiplier

        _.each(this.series, series => {
            if (_.isNil(series) || !series.isAm5SeriesReady() || !series.visible) {
                return
            }

            series.getAm5Series().set("minBulletDistance", Am5Settings.MIN_BULLET_DISTANCE)
            series.getAm5Series().set("autoGapCount", gapCount)
        })

        if (!_.isNil(scrollSeries)) {
            scrollSeries.set("minBulletDistance", 1000)
            scrollSeries.set("autoGapCount", gapCount)
        }
    }

    create(root, start, end, timezone) {
        this.root = root
        this.series = []
        this.chart = null
        this.timezone = timezone

        this.start = _.clone(start)
        this.end = _.clone(end)

        this.axis = null
    }

    dispose() {
        if (!_.isNil(this.axis)) {
            this.axis.dispose()
            this.axis = null
        }
    }
}
